import React from "react";
import { Modal } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import PhotoGalleryForm from "~/pages/Editor/EditorPhotoGallery/modals/PhotoGalleryForm";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  PhotoGallerySection,
  useCreatePhotoGallerySectionMutation,
  useUpdatePhotoGallerySectionMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

const formId = "PhotoGalleryForm";

interface PhotoGalleryModalProps {
  reFetch: () => void;
  currentItem?: PhotoGallerySection;
}

const PhotoGalleryModal = ({
  reFetch,
  currentItem,
}: PhotoGalleryModalProps) => {
  const { t } = useTranslation();
  const { openModal, currentId } = useURLParamsGetter<{
    openModal: string;
    currentId: string;
  }>();
  const setParams = useURLParamsSetter(true);
  const isOpenCreateModal = openModal === "Create";
  const isOpenEditModal = openModal === "Edit";

  const { notifySuccess } = useNotify();

  // Handlers
  const closeModal = () =>
    setParams({ openModal: undefined, currentId: undefined });

  const [createPhotoGallery] = useCreatePhotoGallerySectionMutation({
    onCompleted: () => {
      reFetch();
      notifySuccess(t("photoGallery.createSection"));
      closeModal();
    },
  });
  const [updatePhotoGallery] = useUpdatePhotoGallerySectionMutation({
    onCompleted: () => {
      reFetch();
      notifySuccess(t("photoGallery.editSection"));
      closeModal();
    },
  });
  const onSubmit = ({
    title,
    description,
  }: {
    title: string;
    description: string;
  }) => {
    if (currentId) {
      updatePhotoGallery({
        variables: {
          updatePhotoGallerySectionId: currentId,
          title,
          description,
        },
      });
    } else {
      createPhotoGallery({
        variables: { title, description },
      });
    }
  };

  return (
    <Modal
      title={t("photoGallery.sectionModal")}
      open={isOpenCreateModal || isOpenEditModal}
      onCancel={closeModal}
      closable
      centered
      okButton={{
        htmlType: "submit",
        form: formId,
        children: t("ui.save"),
      }}
    >
      <PhotoGalleryForm
        currentItem={isOpenEditModal ? currentItem : undefined}
        onSubmit={onSubmit}
        formId={formId}
      />
    </Modal>
  );
};

export default PhotoGalleryModal;
